import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { http, formatApiError } from "@/lib/auth";
import { translations } from "@/lib/i18n";
import { photoUrl, FUELS, BODIES, TRANSMISSIONS } from "@/lib/vehicles";

const L = translations.en.vehicles;
const BLANK = { make: "", model: "", variant: "", year: new Date().getFullYear(), mileage_km: 0, fuel: "petrol", transmission: "manual", body_type: "suv", color: "", power_hp: "", quantity: 1, location: "", description: "", photos: [], status: "available", published: true };
const input = "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#2F7BFF] focus:ring-2 focus:ring-[#2F7BFF]/20";

const F = ({ label, children, wide }) => (
  <label className={`block ${wide ? "sm:col-span-2" : ""}`}><span className="mb-1 block text-xs font-bold uppercase tracking-wider text-slate-500">{label}</span>{children}</label>
);

export const VehicleFormDialog = ({ vehicle, onClose, onSaved }) => {
  const [form, setForm] = useState({ ...BLANK, ...vehicle });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const set = (k, num) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : num ? e.target.value.replace(/\D/g, "") : e.target.value });
  const sel = (k, opts, labels) => (
    <select data-testid={`vehicle-form-${k}`} className={input} value={form[k]} onChange={set(k)}>{opts.map((o) => <option key={o} value={o}>{labels[o]}</option>)}</select>
  );

  const upload = async (files) => {
    setUploading(true);
    try {
      const paths = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        paths.push((await http.post("/admin/uploads", fd)).data.path);
      }
      setForm((f) => ({ ...f, photos: [...f.photos, ...paths] }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    const { id, is_sample, created_at, updated_at, price_eur, vat_deductible, ...rest } = form;
    const body = { ...rest, year: +form.year, mileage_km: +form.mileage_km, quantity: +form.quantity || 1, power_hp: form.power_hp === "" || form.power_hp == null ? null : +form.power_hp };
    if (!body.make.trim() || !body.model.trim()) return toast.error("Make and model are required");
    setSaving(true);
    try {
      const { data } = id ? await http.put(`/admin/vehicles/${id}`, body) : await http.post("/admin/vehicles", body);
      toast.success("Vehicle saved");
      onSaved(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="vehicle-form-dialog" className="max-h-[92vh] overflow-y-auto bg-white text-slate-900 sm:max-w-2xl">
        <DialogHeader><DialogTitle className="font-display text-2xl text-[#0A2540]">{vehicle.id ? "Edit vehicle" : "Add vehicle"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid gap-4 sm:grid-cols-2">
          <F label="Make *"><input data-testid="vehicle-form-make" className={input} value={form.make} onChange={set("make")} /></F>
          <F label="Model *"><input data-testid="vehicle-form-model" className={input} value={form.model} onChange={set("model")} /></F>
          <F label="Variant / trim" wide><input data-testid="vehicle-form-variant" className={input} value={form.variant || ""} onChange={set("variant")} /></F>
          <F label="Year"><input data-testid="vehicle-form-year" className={input} value={form.year} onChange={set("year", true)} /></F>
          <F label="Mileage (km)"><input data-testid="vehicle-form-mileage" className={input} value={form.mileage_km} onChange={set("mileage_km", true)} /></F>
          <F label="Fuel">{sel("fuel", FUELS, L.fuel)}</F>
          <F label="Transmission">{sel("transmission", TRANSMISSIONS, L.transmission)}</F>
          <F label="Body type">{sel("body_type", BODIES, L.body)}</F>
          <F label="Power (hp)"><input data-testid="vehicle-form-power" className={input} value={form.power_hp ?? ""} onChange={set("power_hp", true)} /></F>
          <F label="Colour"><input data-testid="vehicle-form-color" className={input} value={form.color || ""} onChange={set("color")} /></F>
          <F label="Location"><input data-testid="vehicle-form-location" className={input} value={form.location || ""} onChange={set("location")} /></F>
          <F label="Units available"><input data-testid="vehicle-form-quantity" className={input} value={form.quantity} onChange={set("quantity", true)} /></F>
          <F label="Status">{sel("status", ["available", "reserved", "sold"], L.status)}</F>
          <div className="flex flex-col justify-end gap-2 text-sm text-slate-700">
            <label className="flex items-center gap-2"><input data-testid="vehicle-form-published" type="checkbox" checked={form.published} onChange={set("published")} />Visible on website</label>
          </div>
          <F label="Description" wide><textarea data-testid="vehicle-form-description" rows={3} className={`${input} resize-none`} value={form.description || ""} onChange={set("description")} /></F>
          <div className="sm:col-span-2">
            <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-500">Photos (first = main)</span>
            <div className="flex flex-wrap gap-2">
              {form.photos.map((p) => (
                <div key={p} className="relative h-20 w-28 overflow-hidden rounded-lg bg-slate-100">
                  <img src={photoUrl(p)} alt="" className="h-full w-full object-cover" />
                  <button type="button" data-testid="vehicle-form-remove-photo" onClick={() => setForm({ ...form, photos: form.photos.filter((x) => x !== p) })} className="absolute right-1 top-1 rounded-full bg-black/60 p-1 text-white"><X className="h-3 w-3" /></button>
                </div>
              ))}
              <label className="flex h-20 w-28 cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-slate-300 text-xs font-semibold text-slate-500 hover:border-[#2F7BFF] hover:text-[#2F7BFF]">
                {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Upload className="h-5 w-5" />Upload</>}
                <input data-testid="vehicle-form-photo-input" type="file" accept="image/jpeg,image/png,image/webp" multiple hidden disabled={uploading} onChange={(e) => { upload([...e.target.files]); e.target.value = ""; }} />
              </label>
            </div>
          </div>
          <div className="flex justify-end gap-2 sm:col-span-2">
            <button type="button" onClick={onClose} className="rounded-full border border-slate-200 px-5 py-2.5 text-sm font-bold text-[#0A2540] hover:bg-slate-50">Cancel</button>
            <button data-testid="vehicle-form-save" disabled={saving || uploading} className="inline-flex items-center gap-2 rounded-full bg-[#2F7BFF] px-6 py-2.5 text-sm font-bold text-white hover:bg-[#1F63E0] disabled:opacity-60">{saving && <Loader2 className="h-4 w-4 animate-spin" />}Save vehicle</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
